import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';
import {
  IZraInvoiceStatusResponse,
  IBulkOrderStatusRequest,
  ICreateZraInvoiceRequest,
  IZraApiResponse,
  IZraPagedResponse,
  IZraItem,
  IZraInvoice,
  IZraClassificationCode,
} from './zra-invoice.interface';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ZraInvoiceService {
  private base: string;
  private version: string;

  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private httpClient: HttpClient
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  /**
   * Log request details
   */
  private logRequest(method: string, endpoint: string, payload?: any): void {
    console.log(`[ZRA Service] ${method} Request:`, {
      endpoint,
      timestamp: new Date().toISOString(),
      payload: payload || 'No payload',
    });
  }

  /**
   * Log response details
   */
  private logResponse(method: string, endpoint: string, response: any): void {
    console.log(`[ZRA Service] ${method} Response:`, {
      endpoint,
      timestamp: new Date().toISOString(),
      response,
    });
  }

  /**
   * Log error details
   */
  private logError(method: string, endpoint: string, error: any): void {
    console.error(`[ZRA Service] ${method} Error:`, {
      endpoint,
      timestamp: new Date().toISOString(),
      error: error?.error || error?.message || error,
      status: error?.status,
      statusText: error?.statusText,
    });
  }

  /**
   * Get the submission status for a specific order
   */
  getSubmissionStatus(entityId: string, orderId: string) {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/ZraInvoice/orders/${orderId}/status`;
    console.log('🌐 [ZRA SERVICE] GET Request:', endpoint);
    this.logRequest('GET', endpoint);

    return this.httpClient.get<IZraInvoiceStatusResponse>(endpoint).pipe(
      tap((response) => {
        console.log('✅ [ZRA SERVICE] Response received:', response);
        this.logResponse('GET', endpoint, response);
      }),
      catchError((error) => {
        console.error('❌ [ZRA SERVICE] Error:', error);
        this.logError('GET', endpoint, error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get submission statuses for multiple orders
   */
  getSubmissionStatuses(entityId: string, request: IBulkOrderStatusRequest) {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/ZraInvoice/orders/status/list`;
    this.logRequest('POST', endpoint, request);

    return this.httpClient
      .post<IZraInvoiceStatusResponse[]>(endpoint, request)
      .pipe(
        tap((response) => this.logResponse('POST', endpoint, response)),
        catchError((error) => {
          this.logError('POST', endpoint, error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Submit an order invoice to ZRA
   */
  submitOrderInvoice(
    entityId: string,
    orderId: string,
    request: ICreateZraInvoiceRequest
  ) {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/ZraInvoice/orders/${orderId}/submit`;
    this.logRequest('POST', endpoint, request);

    return this.httpClient
      .post<IZraInvoiceStatusResponse>(endpoint, request)
      .pipe(
        tap((response) => this.logResponse('POST', endpoint, response)),
        catchError((error) => {
          this.logError('POST', endpoint, error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Queue a sync for multiple orders
   * @returns the job id
   */
  queueSync(entityId: string, request: IBulkOrderStatusRequest) {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/ZraInvoice/batchSync`;
    this.logRequest('POST', endpoint, request);

    return this.httpClient.post<string>(endpoint, request).pipe(
      tap((response) => this.logResponse('POST', endpoint, response)),
      catchError((error) => {
        this.logError('POST', endpoint, error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get the ZRA invoice PDF for a specific order
   * @returns blob data for the PDF
   */
  getInvoicePdf(entityId: string, orderId: string) {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/ZraInvoice/orders/${orderId}/pdf`;
    this.logRequest('GET', endpoint);

    return this.httpClient
      .get(endpoint, { responseType: 'blob' })
      .pipe(
        tap((response) =>
          this.logResponse('GET', endpoint, {
            type: 'Blob',
            size: response.size,
          })
        ),
        catchError((error) => {
          this.logError('GET', endpoint, error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get the ZRA credit note PDF for a specific order
   * @returns blob data for the PDF
   */
  getCreditNotePdf(entityId: string, orderId: string) {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/ZraCreditNote/orders/${orderId}/pdf`;
    this.logRequest('GET', endpoint);

    return this.httpClient
      .get(endpoint, { responseType: 'blob' })
      .pipe(
        tap((response) =>
          this.logResponse('GET', endpoint, {
            type: 'Blob',
            size: response.size,
          })
        ),
        catchError((error) => {
          this.logError('GET', endpoint, error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Submit an order debit note to ZRA
   */
  submitOrderDebitNote(
    entityId: string,
    orderId: string,
    request: ICreateZraInvoiceRequest
  ) {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/ZraDebitNote/orders/${orderId}/submit`;
    this.logRequest('POST', endpoint, request);

    return this.httpClient
      .post<IZraInvoiceStatusResponse>(endpoint, request)
      .pipe(
        tap((response) => this.logResponse('POST', endpoint, response)),
        catchError((error) => {
          this.logError('POST', endpoint, error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get the ZRA debit note PDF for a specific order
   * @returns blob data for the PDF
   */
  getDebitNotePdf(entityId: string, orderId: string) {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/ZraDebitNote/orders/${orderId}/pdf`;
    this.logRequest('GET', endpoint);

    return this.httpClient
      .get(endpoint, { responseType: 'blob' })
      .pipe(
        tap((response) =>
          this.logResponse('GET', endpoint, {
            type: 'Blob',
            size: response.size,
          })
        ),
        catchError((error) => {
          this.logError('GET', endpoint, error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get ZRA items for an entity (paged)
   */
  getZraItems(entityId: string, page: number = 1, pageSize: number = 10) {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/zra-summary/items/${page}/${pageSize}`;
    this.logRequest('GET', endpoint);

    return this.httpClient
      .get<IZraPagedResponse<IZraItem>>(endpoint)
      .pipe(
        tap((response) => this.logResponse('GET', endpoint, response)),
        catchError((error) => {
          this.logError('GET', endpoint, error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get ZRA invoices for an entity (paged)
   */
  getZraInvoices(entityId: string, page: number = 1, pageSize: number = 10) {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/zra-summary/invoices/${page}/${pageSize}`;
    this.logRequest('GET', endpoint);

    return this.httpClient
      .get<IZraPagedResponse<IZraInvoice>>(endpoint)
      .pipe(
        tap((response) => this.logResponse('GET', endpoint, response)),
        catchError((error) => {
          this.logError('GET', endpoint, error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get ZRA classification codes.
   * Note: This endpoint returns a very large response. Response logging is
   * intentionally skipped to avoid crashing the browser console.
   */
  getClassificationCodes(entityId: string) {
    const endpoint = `${this.base}api/v${this.version}/${entityId}/zra-summary/classification-codes`;
    this.logRequest('GET', endpoint);

    return this.httpClient
      .get<IZraApiResponse<IZraClassificationCode[]>>(endpoint)
      .pipe(
        catchError((error) => {
          this.logError('GET', endpoint, error);
          return throwError(() => error);
        })
      );
  }
}
